
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {Ingredient} from "@/models/Ingredient/Ingredient";
import {mapGetters, mapState} from "vuex";
import {NutrientType,} from "@/models/Ingredient/NutrientType";
import {UnitOption} from "@/models/Ingredient/UnitOption";
import {MergedNutrient, NutrientsGroup, NutrientUnitList} from "@/models/Ingredient/NutrientUnitList";
import {IngredientMeasurementEnum} from "@/models/Ingredient/IngredientMeasurementEnum";
import IngredientsRepository from "@/utils/API/IngredientsRepository";
import {debounce} from "@/utils/Helpers";
import vModal from "@/components/vModal.vue";
import VIngredientNutrientQuantityInputRow from "@/components/vIngredientNutrientQuantityInputRow.vue";
import vTabbedContent from "@/components/vTabbedContent.vue";
import {NUTRIENTS_FETCH} from "@/store/ActionTypes";
import {NutrientUnitWithValue} from "@/models/Nutrient/NutrientUnitWithValue";
import {
  OUNCE_TO_GRAMS,
  FLUID_OUNCE_TO_MILLILITERS
} from '@/utils/constants';
import AllergensConfiguration from "@/components/labelConfiguration/AllergensConfiguration.vue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {AllergenSelectionStatus, SavedAllergen, SelectedAllergen} from "@/models/Allergen/SelectedAllergen";
import {AllergenDefinition} from "@/models/Allergen/AllergenDefinition";
import AllergenRepository from "@/utils/API/AllergenRepository";
import {MlAnalysisResponse} from "@/models/AllergenML/AllergenML";
import {AddOnType} from "@/models/Subscriptions/AddOns/AddOnType";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";

type SelectObj = { id: number; name: string };
type DensityObj = { id: number; category: string };
type ImportedMeasurementDraft = { name: string; weightInGrams: number };
type ImportedNutrientDraft = { name: string; value: number; unit?: string; nutrientType?: NutrientType | null };
type ImportedIngredientDraft = {
  name?: string;
  quantity?: number;
  unitLabel?: string;
  unitOfMeasurement?: IngredientMeasurementEnum | null;
  subIngredients?: string;
  allergens: string[];
  mayContain: string[];
  measurements: ImportedMeasurementDraft[];
  nutrients: ImportedNutrientDraft[];
  unmatchedUnits: string[];
  unknownNutrients: string[];
  unmappedFields: string[];
};

interface ErrorStates {
  [key: number]: boolean;
}

const MAX_IMPORT_FILE_SIZE_BYTES = 5 * 1024 * 1024;

@Component({
  components: {
    vModal,
    VUpsellModal,
    VIngredientNutrientQuantityInputRow,
    vTabbedContent,
    AllergensConfiguration
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
    ...mapGetters('nutrients', ['getNutrients']),
  },
})
export default class vIngredientManagement extends Vue {
  @Prop() editMode!: Boolean;
  @Prop() ingredient!: Ingredient;
  @Prop({ default: 0 }) postSaveAiApplyToken!: number;
  @Prop({ default: 0 }) postSavePropagationReviewToken!: number;

  selectedAllergens: SavedAllergen[] = [];
  mayContainText: Partial<Record<LabelLanguage, string[]>> = {};
  ingredientAllergenLanguage: keyof typeof LabelLanguage = "English";
  aiAutoApplyToken = 0;
  aiAutoApplyMode: "detect" | "reapply" = "detect";
  pendingAiAutoAnalyze = false;
  pendingAiAutoAnalyzeNotification = false;
  skipNextAiSaveBeforeOpen = false;

  weightMeasurments: SelectObj[] = [
    { id: IngredientMeasurementEnum.Gram, name: "g" },
    { id: IngredientMeasurementEnum.Milliliter, name: "ml" },
    { id: IngredientMeasurementEnum.Ounce, name: "oz" },
    { id: IngredientMeasurementEnum.FluidOunce, name: "fl. oz" },
  ];
  densityOptions: DensityObj[] = [];
  densityId: number | string = "";
  showWarningModal = false;
  showTrialUpsellModal = false;
  defaultTrialUpsellMessage = "This feature requires a paid subscription to use.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;
  localizationState: any;
  accountState: any;
  categories: { id: number; name: string }[] = [];
  mergedNutrientGroup: { [category: string]: MergedNutrient[] } = {};
  nutrientSourceGroup : NutrientsGroup | null = null;
  errorStates: ErrorStates = {};
  uploadedImportFile: File | null = null;
  importStatusMessage = "";
  isProcessingImport = false;
  importInputId = `ingredient-import-${Math.random().toString(36).slice(2, 11)}`;
  showImportPreviewModal = false;
  importPreviewDraft: ImportedIngredientDraft | null = null;

  translationOrFallback(key: string, fallback: string) {
    const value = this.translations.get(key);
    return !value || value === "---" ? fallback : value;
  }

  provide() {
    return {
      bopFetchPreview: () => {},
    };
  }

  /**
   * Make ingredient enum available in template, the scope of the template is limited to the component instance members.
   */
  public get ingredientMeasurementEnum(): typeof IngredientMeasurementEnum {
    return IngredientMeasurementEnum;
  }

  get isTrialUser() {
    return this.accountState.userDetails.isTrial;
  }

  get allergenStatusLegendTooltipContent() {
    return [
      "<div class='allergens-popover-legend'>",
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge ai'><i class='fa fa-check'></i></span><span>${this.translations.get("allergens_ai_legend_ai") || "AI detected this allergen."}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge removed'><i class='fa fa-times'></i></span><span>${this.translations.get("allergens_ai_legend_removed") || "Manually marked as not an allergen."}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge user'><i class='fa fa-check'></i><span class='allergens-popover-badge-accent'><i class='fa fa-question'></i></span></span><span>${this.translations.get("allergens_ai_legend_user_added") || "Manually added as an allergen."}</span></div>`,
      "</div>"
    ].join("");
  }

  get isAllergenAiAddonActive() {
    if (this.isTrialUser) {
      return true;
    }

    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  get showAllergenAiButton() {
    return this.isTrialUser || this.isAllergenAiAddonActive;
  }

  get showIngredientDensity() {
    return this.ingredient.unitOfMeasurement == IngredientMeasurementEnum.Milliliter
      || this.ingredient.unitOfMeasurement == IngredientMeasurementEnum.FluidOunce;
  }

  get shouldShowMeasurementsSpacer() {
    return false;
  }

  get shouldShowIngredientAiDetector() {
    return Boolean(this.ingredient?.id && !this.ingredient.hasAllergenAnalysis);
  }

  @Watch("postSaveAiApplyToken")
  onPostSaveAiApplyTokenUpdated(value: number) {
    if (!value || !this.pendingAiAutoAnalyze) {
      return;
    }

    this.pendingAiAutoAnalyze = false;
    this.pendingAiAutoAnalyzeNotification = true;
    this.skipNextAiSaveBeforeOpen = true;
    this.aiAutoApplyToken += 1;
  }

  async beforeOpenIngredientAiDetector() {
    if (this.skipNextAiSaveBeforeOpen) {
      this.skipNextAiSaveBeforeOpen = false;
      return true;
    }

    if (this.ingredient) {
      this.aiAutoApplyMode = this.ingredient.hasAllergenAnalysis ? "reapply" : "detect";
      this.pendingAiAutoAnalyze = true;
      this.submit({ openAiAfterSave: true });
      return false;
    }

    return true;
  }

  /**
   * Method for fetching ingredient density options based on measurement
   */
  searchCategoryDensities(searchQuery: string) {
    // Check if the density category has changed and update the value so that it is available as a dropdown option
    this.densityId = this.ingredient.categoryDensityId
      ? this.ingredient.categoryDensityId
      : "";
    debounce(
      () => {
        IngredientsRepository.searchCategoryDensities(searchQuery, this.densityId)
          .then((response: any) => {
            this.densityOptions = response.data.result;
          })
          .catch((error) => {
            if (error.response) {
              this.$snotify.error(
                error.response.data.errorMessages[0].userVisibleMessage
              );
            }
            else this.$snotify.error(this.translations.get("network-error"));
          });
      },
      600,
      "search-category-densities"
    );
  }

  pushNutrient(nutrient: MergedNutrient, nutrientList: NutrientUnitWithValue[]) {
    if (nutrient.value !== null && nutrient.value !== undefined) {
      nutrientList.push({
        value: nutrient.value,
        nutrientUnitId: nutrient.nutrientUnitId,
      });
    }

    // Handle children
    if (nutrient.childNutrients) {
      nutrient.childNutrients.forEach((childNutrient) => {
        this.pushNutrient(childNutrient, nutrientList);
      });
    }
  }

  submit(options?: { openAiAfterSave?: boolean }) {
    this.ingredient.allergens = [...this.selectedAllergens];
    this.ingredient.mayContainText = { ...this.mayContainText };
    const skipCalculationWarning = Boolean(options?.openAiAfterSave);

    // Create the nutrients array based on merged data
    const nutrients: NutrientUnitWithValue[] = [];

    let energyValue = 0;

    // Iterate through all categories and nutrients
    Object.values(this.mergedNutrientGroup).forEach((nutrientList) => {
      nutrientList.forEach((nutrient) => {
        if(nutrient.nutrientType == NutrientType.Energy){
          energyValue = nutrient.value;
        }

        this.pushNutrient(nutrient, nutrients);
      });
    });

    const sugarsValue = this.mergedNutrientGroup["sugars"].find(
      (nutrient) => nutrient.nutrientType === NutrientType.Sugars
    );

    const vitaminAValue = this.mergedNutrientGroup["vitamins"].find(
      (nutrient) => nutrient.nutrientType === NutrientType.VitaminA
    );

    const moistureValue = this.mergedNutrientGroup["others"].find(
      (nutrient) => nutrient.nutrientType === NutrientType.Moisture
    );

    if (sugarsValue.value < sugarsValue.childNutrients[0].value) {
      this.$snotify.error(
        this.translations.get("AddedSugarIsLessThanEqualToSugars")
      );
      return;
    }

    if(vitaminAValue.value < this.calculateTotalChildNutrientsValue(vitaminAValue.childNutrients)){
      this.$snotify.error(
        this.translations.get("VitaminAIsLessThanEqualToRetinolBetaCarotene")
      );
      return;
    }

    if (moistureValue.value > this.ingredient.weightInGrams) {
      this.$snotify.error(
        this.translations.get("moisture_loss_less_than_weight")
      );
      return;
    }

    // Validation and submission logic
    this.$validator.validateAll().then((result) => {
      if (result) {
        if (
          skipCalculationWarning ||
          this.specifiedSugarAlcohols != 0 ||
          energyValue != 0 ||
          this.showWarningModal
        ) {
          this.showWarningModal = false;
          this.$emit("submit", this.ingredient, nutrients, this.selectedAllergens, this.mayContainText, options);
          return;
        }

        else {
          this.showWarningModal = true;
          return;
        }
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  cancel() {
    this.$emit("cancel");
  }

  /**
   * Method used in create / edit ingredient which pushes empty measurement in
   * the ingredient for the user to populate
   */
  addMeasurement() {
    // we use length as id as it will always be unique locally
    let length = this.ingredient.allMeasurements.length;
    this.ingredient.allMeasurements.push({
      id: length,
      name: "",
      weightInGrams: 0,
    });
  }

  /**
   * Method used in create / edit ingredient which removes specific measurement in the ingredient
   *
   * @param {number} id
   */
  removeMeasurement(id: number) {
    // find the measurement with id from params
    let measurement = this.ingredient.allMeasurements.find((m) => m.id === id);
    if (measurement) {
      // find it's index in the array of measurements
      let index = this.ingredient.allMeasurements.indexOf(measurement);
      // delete it by deleting 1 item on position 'index'
      this.ingredient.allMeasurements.splice(index, 1);
    }
  }

  updateWeightInGrams(){
    switch (this.ingredient.unitOfMeasurement) {
    case IngredientMeasurementEnum.Gram:
    case IngredientMeasurementEnum.Milliliter:
      this.ingredient.weightInGrams = +this.ingredient.quantityInUnitOfMeasurement;
      break;
    case IngredientMeasurementEnum.Ounce:
      this.ingredient.weightInGrams = this.ingredient.quantityInUnitOfMeasurement * OUNCE_TO_GRAMS;
      break;
    case IngredientMeasurementEnum.FluidOunce:
      this.ingredient.weightInGrams = this.ingredient.quantityInUnitOfMeasurement * FLUID_OUNCE_TO_MILLILITERS;
      break;
    default:
      break;
    }
  }

  unitOptions(nutri : NutrientUnitList): UnitOption[]{
    let list = nutri.units
      ? nutri.units.map((u) => ({
        id: u.nutrientUnitId ? u.nutrientUnitId : 0,
        name: u.unit ? u.unit.name : "",
        isDefault:u.isDefault
      }))
      : [];
    if (list) return list;
  }

  openImportPicker() {
    const input = this.$refs.importFileInput as HTMLInputElement | undefined;
    if (input) {
      input.click();
    }
  }

  onImportFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    const selectedFile = files.length ? files[0] : null;

    if (!selectedFile) {
      this.uploadedImportFile = null;
      this.importStatusMessage = "";
      return;
    }

    const extension = this.getFileExtension(selectedFile.name).toLowerCase();
    const allowedExtensions = ["png", "jpg", "jpeg", "pdf"];
    const isAllowedType = allowedExtensions.includes(extension);

    if (!isAllowedType) {
      this.uploadedImportFile = null;
      this.importStatusMessage = "Only PDF, JPG, and PNG files are allowed.";
      this.$snotify.error("Only image or PDF files can be uploaded.");
      input.value = "";
      return;
    }

    if (selectedFile.size > MAX_IMPORT_FILE_SIZE_BYTES) {
      this.uploadedImportFile = null;
      this.importStatusMessage = "File size must be 5 MB or less.";
      this.$snotify.error("Please upload a file smaller than 5 MB.");
      input.value = "";
      return;
    }

    this.uploadedImportFile = selectedFile;
    this.importStatusMessage = this.uploadedImportFile
      ? this.translationOrFallback("ingr_custom_import_process", "Process")
      : "";
  }

  cancelImportSelection() {
    this.uploadedImportFile = null;
    this.importStatusMessage = "";

    const input = this.$refs.importFileInput as HTMLInputElement | undefined;
    if (input) {
      input.value = "";
    }
  }

  resetCustomIngredientForm() {
    this.clearCustomIngredientFormState();
    this.cancelImportSelection();
    this.$validator.reset();
    this.$snotify.success("The file has been removed.");
  }

  clearCustomIngredientFormState() {
    this.ingredient.name = "";
    this.ingredient.subIngredients = "";
    this.ingredient.quantityInUnitOfMeasurement = 100;
    this.ingredient.unitOfMeasurement = IngredientMeasurementEnum.Gram;
    this.ingredient.weightInGrams = 100;
    this.ingredient.categoryDensityId = null;
    this.ingredient.allMeasurements = [];
    this.ingredient.allergens = [];
    this.ingredient.mayContainText = {};

    this.selectedAllergens = [];
    this.mayContainText = {};
    this.showImportPreviewModal = false;
    this.importPreviewDraft = null;

    Object.values(this.mergedNutrientGroup).forEach((nutrientList) => {
      nutrientList.forEach((nutrient) => {
        nutrient.value = 0;
        nutrient.childNutrients?.forEach((childNutrient) => {
          childNutrient.value = 0;
        });
      });
    });

    this.onNutrientModelChange();
  }

  formatUploadedFileMeta(file: File) {
    const sizeInKb = Math.max(1, Math.round(file.size / 1024));
    const extension = this.getFileExtension(file.name).toUpperCase() || "FILE";
    return `${extension} · ${sizeInKb} KB`;
  }

  async processImportFile() {
    if (!this.uploadedImportFile) {
      return;
    }

    this.isProcessingImport = true;
    this.importStatusMessage = this.translationOrFallback("ingr_custom_import_processing", "Processing...");

    try {
      const importedDraft = await this.extractImportedIngredientDraft(this.uploadedImportFile);
      this.importPreviewDraft = this.cloneImportedDraft(importedDraft);
      this.showImportPreviewModal = true;
      this.importStatusMessage = "Review extracted values before confirming.";
    }
    catch (error) {
      this.importStatusMessage = "Unable to process this file.";
      const responseMessage = (error as any)?.response?.data?.errorMessages?.[0]?.userVisibleMessage;
      this.$snotify.error(responseMessage || "Unable to process the uploaded file.");
    }
    finally {
      this.isProcessingImport = false;
    }
  }

  async extractImportedIngredientDraft(file: File): Promise<ImportedIngredientDraft> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await IngredientsRepository.extractCustomIngredientPreview(formData);
    const result = response?.data?.result || {};

    return {
      name: result.name || this.humanizeFileName(file.name),
      quantity: typeof result.quantity === "number" ? result.quantity : undefined,
      unitLabel: result.unitLabel || "",
      unitOfMeasurement: typeof result.unitOfMeasurement === "number" ? result.unitOfMeasurement : null,
      subIngredients: result.subIngredients || "",
      allergens: this.normalizeImportedAllergens(Array.isArray(result.allergens) ? result.allergens : []),
      mayContain: Array.isArray(result.mayContain) ? [...result.mayContain] : [],
      measurements: Array.isArray(result.measurements) ? result.measurements.map((measurement: any) => ({
        name: measurement.name || "",
        weightInGrams: Number(measurement.weightInGrams) || 0,
      })) : [],
      nutrients: Array.isArray(result.nutrients) ? result.nutrients.map((nutrient: any) => ({
        name: nutrient.name || "",
        value: Number(nutrient.value) || 0,
        unit: nutrient.unit || "",
        nutrientType: typeof nutrient.nutrientType === "number" ? nutrient.nutrientType : null,
      })) : [],
      unmatchedUnits: Array.isArray(result.unmatchedUnits) ? [...result.unmatchedUnits] : [],
      unknownNutrients: Array.isArray(result.unknownNutrients) ? [...result.unknownNutrients] : [],
      unmappedFields: Array.isArray(result.unmappedFields) ? [...result.unmappedFields] : [],
    };
  }

  async applyImportedDraft(importedDraft: ImportedIngredientDraft): Promise<boolean> {
    let hasReviewItems = this.hasImportedDraftReviewItems(importedDraft);
    if (importedDraft.name) {
      this.ingredient.name = importedDraft.name;
    }
    else {
      hasReviewItems = true;
    }

    if (importedDraft.subIngredients) {
      this.ingredient.subIngredients = importedDraft.subIngredients;
    }

    if (typeof importedDraft.quantity === "number" && importedDraft.unitLabel) {
      const resolvedMeasurement = importedDraft.unitOfMeasurement ?? this.resolveIngredientMeasurement(importedDraft.unitLabel);
      if (resolvedMeasurement !== null) {
        this.ingredient.quantityInUnitOfMeasurement = importedDraft.quantity;
        this.ingredient.unitOfMeasurement = resolvedMeasurement;
        this.updateWeightInGrams();
      }
      else {
        hasReviewItems = true;
      }
    }

    if (importedDraft.measurements.length) {
      this.ingredient.allMeasurements = importedDraft.measurements.map((measurement, index) => ({
        id: index,
        name: measurement.name,
        weightInGrams: measurement.weightInGrams,
      }));
    }

    let matchedNutrientsCount = 0;
    importedDraft.nutrients.forEach((nutrient) => {
      const matchedType = nutrient.nutrientType ?? this.resolveNutrientType(nutrient.name);
      if (matchedType === null) {
        hasReviewItems = true;
        return;
      }

      const mergedNutrient = this.findMergedNutrientByType(matchedType);
      if (!mergedNutrient) {
        hasReviewItems = true;
        return;
      }

      mergedNutrient.nutrientUnitId = this.resolveImportedNutrientUnitId(mergedNutrient, nutrient.unit);
      mergedNutrient.value = nutrient.value;
      matchedNutrientsCount += 1;
    });

    if (importedDraft.allergens.length) {
      const resolvedAllergens = await this.resolveImportedAllergensToSaved(importedDraft.allergens);
      if (resolvedAllergens.length) {
        this.selectedAllergens = [...resolvedAllergens];
        this.ingredient.allergens = [...resolvedAllergens];
      }
      else {
        hasReviewItems = true;
      }
    }

    if (importedDraft.mayContain.length) {
      this.mayContainText = {
        ...this.mayContainText,
        [LabelLanguage.English]: [...importedDraft.mayContain],
      };
      this.ingredient.mayContainText = { ...this.mayContainText };
    }

    this.onNutrientModelChange();
    return hasReviewItems || (importedDraft.nutrients.length > 0 && matchedNutrientsCount === 0);
  }

  cloneImportedDraft(importedDraft: ImportedIngredientDraft): ImportedIngredientDraft {
    return {
      name: importedDraft.name || "",
      quantity: importedDraft.quantity,
      unitLabel: importedDraft.unitLabel || "",
      unitOfMeasurement: importedDraft.unitOfMeasurement ?? null,
      subIngredients: importedDraft.subIngredients || "",
      allergens: [...importedDraft.allergens],
      mayContain: [...importedDraft.mayContain],
      measurements: importedDraft.measurements.map((measurement) => ({ ...measurement })),
      nutrients: importedDraft.nutrients.map((nutrient) => ({ ...nutrient })),
      unmatchedUnits: [...importedDraft.unmatchedUnits],
      unknownNutrients: [...importedDraft.unknownNutrients],
      unmappedFields: [...importedDraft.unmappedFields],
    };
  }

  hasImportedDraftReviewItems(importedDraft: ImportedIngredientDraft): boolean {
    return importedDraft.unmatchedUnits.length > 0
      || importedDraft.unknownNutrients.length > 0
      || importedDraft.unmappedFields.length > 0;
  }

  closeImportPreviewModal() {
    this.showImportPreviewModal = false;
    this.importPreviewDraft = null;
  }

  async confirmImportPreview() {
    if (!this.importPreviewDraft) {
      return;
    }

    const confirmedDraft = this.cloneImportedDraft(this.importPreviewDraft);
    this.clearCustomIngredientFormState();
    const hasReviewItems = await this.applyImportedDraft(confirmedDraft);

    if (hasReviewItems) {
      this.importStatusMessage = this.translationOrFallback("ingr_custom_import_applied_success", "Imported values applied to the ingredient form.");
      this.$snotify.warning("Imported values applied. Some items still need review.");
    }
    else {
      this.importStatusMessage = this.translationOrFallback("ingr_custom_import_applied_success", "Imported values applied to the ingredient form.");
      this.$snotify.success(this.translationOrFallback("ingr_custom_import_applied_success", "Imported values applied to the ingredient form."));
    }

    this.closeImportPreviewModal();
  }
  resolveIngredientMeasurement(unitLabel: string): IngredientMeasurementEnum | null {
    const normalized = this.normalizeImportLabel(unitLabel);
    const unitMap: Record<string, IngredientMeasurementEnum> = {
      g: IngredientMeasurementEnum.Gram,
      gram: IngredientMeasurementEnum.Gram,
      grams: IngredientMeasurementEnum.Gram,
      ml: IngredientMeasurementEnum.Milliliter,
      milliliter: IngredientMeasurementEnum.Milliliter,
      milliliters: IngredientMeasurementEnum.Milliliter,
      millilitre: IngredientMeasurementEnum.Milliliter,
      millilitres: IngredientMeasurementEnum.Milliliter,
      oz: IngredientMeasurementEnum.Ounce,
      ounce: IngredientMeasurementEnum.Ounce,
      ounces: IngredientMeasurementEnum.Ounce,
      "fl oz": IngredientMeasurementEnum.FluidOunce,
      "fluid ounce": IngredientMeasurementEnum.FluidOunce,
      "fluid ounces": IngredientMeasurementEnum.FluidOunce,
    };

    return unitMap[normalized] ?? null;
  }

  resolveNutrientType(nutrientName: string): NutrientType | null {
    const normalized = this.normalizeImportLabel(nutrientName);
    const nutrientMap: Record<string, NutrientType> = {
      energy: NutrientType.Energy,
      calories: NutrientType.Energy,
      fat: NutrientType.Fat,
      "total fat": NutrientType.Fat,
      "saturated fat": NutrientType.SaturatedFattyAcids,
      saturatedfattyacids: NutrientType.SaturatedFattyAcids,
      saturates: NutrientType.SaturatedFattyAcids,
      "trans fat": NutrientType.TransFattyAcids,
      transfattyacids: NutrientType.TransFattyAcids,
      cholesterol: NutrientType.Cholesterol,
      sodium: NutrientType.Sodium,
      carbohydrate: NutrientType.Carbohydrate,
      carbohydrates: NutrientType.Carbohydrate,
      "total carbohydrate": NutrientType.Carbohydrate,
      "total carbohydrates": NutrientType.Carbohydrate,
      fibre: NutrientType.Fibre,
      fiber: NutrientType.Fibre,
      "dietary fiber": NutrientType.Fibre,
      "dietary fibre": NutrientType.Fibre,
      "total dietary fiber": NutrientType.Fibre,
      "total dietary fibre": NutrientType.Fibre,
      sugars: NutrientType.Sugars,
      sugar: NutrientType.Sugars,
      "total sugar": NutrientType.Sugars,
      "total sugars": NutrientType.Sugars,
      protein: NutrientType.Protein,
      "vitamin d": NutrientType.VitaminD,
      potassium: NutrientType.Potassium,
      calcium: NutrientType.Calcium,
      iron: NutrientType.Iron,
      "added sugar": NutrientType.AddedSugar,
      "added sugars": NutrientType.AddedSugar,
      "sugar alcohols": NutrientType.SugarAlcohols,
      "vitamin c": NutrientType.VitaminC,
      "vitamin a": NutrientType.VitaminA,
      "vitamin e": NutrientType.VitaminE,
      magnesium: NutrientType.Magnesium,
      caffeine: NutrientType.Caffeine,
      moisture: NutrientType.Moisture,
      water: NutrientType.Moisture,
      ash: NutrientType.Ash,
    };

    return nutrientMap[normalized] ?? null;
  }

  findMergedNutrientByType(nutrientType: NutrientType): MergedNutrient | null {
    for (const nutrientList of Object.values(this.mergedNutrientGroup)) {
      for (const nutrient of nutrientList) {
        if (nutrient.nutrientType === nutrientType) {
          return nutrient;
        }

        const matchedChild = (nutrient.childNutrients || []).find(
          (childNutrient) => childNutrient.nutrientType === nutrientType
        );

        if (matchedChild) {
          return matchedChild;
        }
      }
    }

    return null;
  }

  resolveImportedNutrientUnitId(mergedNutrient: MergedNutrient, unit?: string): number | null {
    if (!mergedNutrient.units?.length) {
      return mergedNutrient.nutrientUnitId;
    }

    const normalizedUnit = (unit || "").trim().toLowerCase();
    if (!normalizedUnit) {
      return mergedNutrient.nutrientUnitId;
    }

    const matchingUnit = mergedNutrient.units.find(
      (candidate) => candidate.unit?.name?.trim().toLowerCase() === normalizedUnit
    );

    return matchingUnit?.nutrientUnitId ?? mergedNutrient.nutrientUnitId;
  }

  humanizeFileName(fileName: string) {
    const withoutExtension = fileName.replace(/\.[^.]+$/, "");
    return withoutExtension
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  normalizeImportLabel(value: string) {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  normalizeImportedAllergens(allergens: string[]) {
    const canonicalAllergenPatterns: Array<{ label: string; patterns: string[] }> = [
      { label: "Milk", patterns: ["milk", "dairy", "cream", "butter", "cheese", "casein", "whey"] },
      { label: "Eggs", patterns: ["egg", "eggs", "albumen"] },
      { label: "Fish", patterns: ["fish"] },
      { label: "Crustaceans", patterns: ["crustacean", "crustaceans", "shrimp", "prawn", "crab", "lobster"] },
      { label: "Molluscs", patterns: ["mollusk", "mollusks", "mollusc", "molluscs", "clam", "oyster", "mussel", "scallop", "squid"] },
      { label: "Peanuts", patterns: ["peanut", "peanuts", "groundnut", "groundnuts"] },
      { label: "Soy", patterns: ["soy", "soya", "soybean", "soybeans"] },
      { label: "Tree nuts", patterns: ["tree nut", "tree nuts", "almond", "cashew", "hazelnut", "macadamia", "pecan", "pistachio", "walnut"] },
      { label: "Wheat", patterns: ["wheat", "wheats"] },
      { label: "Gluten", patterns: ["gluten", "barley", "oat", "oats", "triticale", "cereal"] },
      { label: "Sesame", patterns: ["sesame"] },
      { label: "Mustard", patterns: ["mustard"] },
      { label: "Celery", patterns: ["celery"] },
      { label: "Lupin", patterns: ["lupin"] },
      { label: "Sulphites", patterns: ["sulphite", "sulphites", "sulfite", "sulfites"] },
    ];

    const normalizedAllergens = new Set<string>();

    (allergens || []).forEach((allergen) => {
      const normalizedSource = this.normalizeImportLabel(allergen || "");
      if (!normalizedSource) {
        return;
      }

      let matched = false;
      canonicalAllergenPatterns.forEach(({ label, patterns }) => {
        if (patterns.some((pattern) => normalizedSource.includes(this.normalizeImportLabel(pattern)))) {
          normalizedAllergens.add(label);
          matched = true;
        }
      });

      if (!matched) {
        normalizedAllergens.add(allergen);
      }
    });

    return Array.from(normalizedAllergens);
  }

  canonicalizeImportedAllergenLabel(allergen: string) {
    const normalized = this.normalizeImportLabel(allergen || "");
    if (!normalized) {
      return "";
    }

    const canonicalAllergenPatterns: Array<{ label: string; patterns: string[] }> = [
      { label: "milk", patterns: ["milk", "dairy", "cream", "butter", "cheese", "casein", "whey"] },
      { label: "eggs", patterns: ["egg", "eggs", "albumen"] },
      { label: "fish", patterns: ["fish"] },
      { label: "crustaceans", patterns: ["crustacean", "crustaceans", "shrimp", "prawn", "crab", "lobster"] },
      { label: "molluscs", patterns: ["mollusk", "mollusks", "mollusc", "molluscs", "clam", "oyster", "mussel", "scallop", "squid"] },
      { label: "peanuts", patterns: ["peanut", "peanuts", "groundnut", "groundnuts"] },
      { label: "soy", patterns: ["soy", "soya", "soybean", "soybeans"] },
      { label: "tree nuts", patterns: ["tree nut", "tree nuts", "almond", "cashew", "hazelnut", "macadamia", "pecan", "pistachio", "walnut"] },
      { label: "wheat", patterns: ["wheat", "wheats"] },
      { label: "gluten", patterns: ["gluten", "barley", "oat", "oats", "triticale", "cereal"] },
      { label: "sesame", patterns: ["sesame"] },
      { label: "mustard", patterns: ["mustard"] },
      { label: "celery", patterns: ["celery"] },
      { label: "lupin", patterns: ["lupin"] },
      { label: "sulphites", patterns: ["sulphite", "sulphites", "sulfite", "sulfites"] },
    ];

    const matched = canonicalAllergenPatterns.find(({ patterns }) =>
      patterns.some((pattern) => normalized.includes(this.normalizeImportLabel(pattern)))
    );

    return matched?.label || normalized;
  }

  async resolveImportedAllergensToSaved(allergens: string[]): Promise<SavedAllergen[]> {
    if (!allergens.length) {
      return [];
    }

    try {
      const response = await AllergenRepository.listAllergens();
      const allergenOptions = Array.isArray(response.data) ? response.data : [];
      const resolved = new Map<number, SelectedAllergen>();

      allergens.forEach((allergenLabel) => {
        const normalizedLabel = this.canonicalizeImportedAllergenLabel(allergenLabel);
        const matchedOption = allergenOptions.find((option: AllergenDefinition) => {
          const normalizedOptionName = this.canonicalizeImportedAllergenLabel(option.name || "");
          const normalizedOptionCode = this.normalizeImportLabel(option.code || "");

          return normalizedOptionName === normalizedLabel
            || normalizedOptionCode === normalizedLabel
            || normalizedLabel.includes(normalizedOptionName)
            || normalizedLabel.includes(normalizedOptionCode);
        });

        if (!matchedOption) {
          return;
        }

        resolved.set(matchedOption.id, {
          id: matchedOption.id,
          code: matchedOption.code,
          name: matchedOption.name,
          aliasNames: [],
          status: AllergenSelectionStatus.ManuallyAdded,
          aiRecipeAggregateId: null,
        });
      });

      return Array.from(resolved.values());
    }
    catch {
      return [];
    }
  }

  getFileExtension(fileName: string) {
    const parts = fileName.toLowerCase().split(".");
    return parts.length > 1 ? parts.pop() || "" : "";
  }

  get specifiedSugarAlcohols() {
    if (!this.mergedNutrientGroup) {
      return 0;
    }

    const sugarAlcoholNames = [
      'Erythritol',
      'Isomalt',
      'Mannitol',
      'Xylitol',
      'Lactitol',
      'Sorbitol',
      'Maltitol'
    ];

    let sum = 0;

    sugarAlcoholNames.forEach((name) => {
      Object.values(this.mergedNutrientGroup).forEach((nutrientList) => {
        nutrientList.forEach((nutrient) => {
          if (nutrient.name === name) {
            sum += Number(nutrient.value) || 0;
          }
        });
      });
    });

    return Number(sum.toFixed(12));
  }

  get translations() {
    return this.localizationState.translations;
  }

  get importPreviewServingText() {
    if (!this.importPreviewDraft) {
      return this.translationOrFallback("ingr_custom_import_no_value", "No value detected");
    }

    const quantity = typeof this.importPreviewDraft.quantity === "number"
      ? this.importPreviewDraft.quantity
      : null;
    const unitLabel = this.importPreviewDraft.unitLabel || "";

    if (quantity === null && !unitLabel) {
      return this.translationOrFallback("ingr_custom_import_no_value", "No value detected");
    }

    return `${quantity ?? ""} ${unitLabel}`.trim();
  }

  formatPreviewNutrientValue(nutrient: ImportedNutrientDraft) {
    return `${nutrient.value ?? 0}${nutrient.unit ? ` ${nutrient.unit}` : ""}`;
  }

  formatPreviewText(value?: string) {
    if (!value) {
      return "";
    }

    return value.replace(/^[\s:：;,.\-_/\\|]+/, "").trimStart();
  }

  getAllergenAiUpsellMessage() {
    const translation = this.translations.get("upsell_allergen_ai_message");
    return translation === "---"
      ? "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
      : translation;
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  //Watcher to detect changes in the nutrientModel
  @Watch('mergedNutrientGroup', { deep: true, immediate: true })
  onNutrientModelChange() {
    const nutrients = this.mergedNutrientGroup;
    this.errorStates = {};

    if(Object.keys(nutrients).length != 0){
      const sugarsAlcohol = nutrients["sugars"].find(
        (nutrient) => nutrient.nutrientType === NutrientType.SugarAlcohols
      );

      const sugarsValue = nutrients["sugars"].find(
        (nutrient) => nutrient.nutrientType === NutrientType.Sugars
      );

      const vitaminA = nutrients["vitamins"].find(
        (nutrient) => nutrient.nutrientType === NutrientType.VitaminA
      );

      const moisture = nutrients["others"].find(
        (nutrient) => nutrient.nutrientType === NutrientType.Moisture
      );

      if(sugarsAlcohol){
        const totalSugarAlcoholValue = this.calculateTotalChildNutrientsValue(sugarsAlcohol.childNutrients);
        this.updatedTotalNutrientValue(sugarsAlcohol, totalSugarAlcoholValue);
      }

      // Handles quantities of the child item being greater than parent -- which should not be possible
      if (sugarsValue.value < sugarsValue.childNutrients[0].value) {
        this.errorStates[sugarsValue.id] = true;
      }

      if(vitaminA.value < this.calculateTotalChildNutrientsValue(vitaminA.childNutrients)){
        this.errorStates[vitaminA.id] = true;
      }

      if (moisture.value > this.ingredient.weightInGrams) {
        this.errorStates[moisture.id] = true;
      }
    }
  }

  @Watch("ingredient", { deep: true, immediate: true })
  onIngredientChanged(value: Ingredient) {
    this.selectedAllergens = value?.allergens ? [...value.allergens] : [];
    this.mayContainText = value?.mayContainText ? { ...value.mayContainText } : {};
  }

  calculateTotalChildNutrientsValue(nutrients: MergedNutrient[]): number {
    let totalValue = 0;
    nutrients.forEach((childNutrient) => {
      totalValue += childNutrient.value;
    });

    return totalValue;
  }

  updatedTotalNutrientValue(element:MergedNutrient, value: number) {
    element.value = value;
  }

  created() {
    this.initializeSourceNutrients();
    // If in edit mode we need to get the density category as an option for the dropdown (if the ingr has it)
    this.densityId = this.ingredient.categoryDensityId
      ? this.ingredient.categoryDensityId
      : "";
    IngredientsRepository.searchCategoryDensities("", this.densityId)
      .then((response: any) => {
        this.densityOptions = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  initializeSourceNutrients(){
    this.nutrientSourceGroup = this.$store.getters.getNutrients;

    if(!this.nutrientSourceGroup){
      this.$store
        .dispatch(NUTRIENTS_FETCH)
        .then((nutrients: NutrientsGroup) => {
          this.nutrientSourceGroup = nutrients;
          this.assignNutrientSource(this.nutrientSourceGroup);
        })
        .catch((error) => {
          if (error.response) {
            this.$snotify.error(
              error.response.data.errorMessages[0].userVisibleMessage
            );
          }
          else this.$snotify.error(this.translations.get("network-error"));
        });
    }
    else{
      this.assignNutrientSource(this.nutrientSourceGroup);
    }
  }

  assignNutrientSource(nutrientSourceGroup : NutrientsGroup){
    this.categories = this.assignCategorys(Object.keys(nutrientSourceGroup));
    this.mergedNutrientGroup = this.mergeNutrientsWithValues(nutrientSourceGroup, this.ingredient);
  }

  assignCategorys(categoryNames: string[]): { id: number; name: string }[] {
    return categoryNames.map((name, index) => ({
      id: index + 1,
      name: name
    }));
  }

  mergeNutrientsWithValues(
    nutrientGroups: NutrientsGroup,
    ingredient: Ingredient
  ): { [category: string]: MergedNutrient[] } {
    const merged: { [category: string]: MergedNutrient[] } = {};

    // Create a map of nutrient values for quick lookup
    const nutrientToNutrientValueMap: { [nutrientId: number]: number | null } = {};
    const nutrientToNutrientUnitMap: { [nutrientId: number]: number | null } = {};

    Object.values(ingredient.nutrients).forEach(({ ingredientNutrient }) => {
      nutrientToNutrientValueMap[ingredientNutrient.nutrientUnit.nutrientId] = ingredientNutrient.value;
      nutrientToNutrientUnitMap[ingredientNutrient.nutrientUnit.nutrientId] = ingredientNutrient.nutrientUnitId;
    });

    // Merge nutrient details with their values
    Object.keys(nutrientGroups).forEach((categoryName) => {
      merged[categoryName] = nutrientGroups[categoryName].map((nutrient) => ({
        ...nutrient,
        value: nutrientToNutrientValueMap[nutrient.id || 0] || 0,
        nutrientUnitId: nutrientToNutrientUnitMap[nutrient.id] || nutrient.units[0].nutrientUnitId,
        childNutrients: nutrient.childNutrients.map((childNutrient) => ({
          ...childNutrient,
          value: nutrientToNutrientValueMap[childNutrient.id || 0] || 0,
          nutrientUnitId: nutrientToNutrientUnitMap[childNutrient.id || 0] || (childNutrient.units && childNutrient.units.length > 0 ? childNutrient.units[0].nutrientUnitId : null)
        })) || null
      }));
    });

    return merged;
  }

  onAllergenValueUpdated(value: SavedAllergen[]) {
    this.selectedAllergens = [...value];
    this.ingredient.allergens = [...value];
  }

  onAllergenAliasesSaved() {
    this.$emit("save-allergen-aliases", this.ingredient.id);
  }

  onMayContainTextUpdated(value: Partial<Record<LabelLanguage, string[]>>) {
    this.mayContainText = { ...value };
    this.ingredient.mayContainText = { ...value };
  }

  onIngredientAiAnalysisApplied(result: MlAnalysisResponse) {
    if (!this.ingredient) {
      return;
    }

    this.ingredient.hasAllergenAnalysis = true;
    this.ingredient.hasAllergen = this.analysisResultHasDetectedAllergen(result);
  }

  onIngredientAiAnalysisRunCompleted() {
    if (!this.pendingAiAutoAnalyzeNotification) {
      return;
    }

    this.pendingAiAutoAnalyzeNotification = false;
    this.$snotify.success("Custom ingredient was successfully saved and analyzed.");
  }

  onIngredientAiDetectorRequestFinished() {
    this.skipNextAiSaveBeforeOpen = false;
    this.aiAutoApplyMode = "detect";
  }

  onPropagationReviewFinished() {
    this.$emit("propagation-review-finished");
  }

  analysisResultHasDetectedAllergen(result: MlAnalysisResponse) {
    if ((result.ingredients || []).length > 0) {
      return result.ingredients.some((ingredientResult) =>
        this.analysisEntryHasDetectedAllergen(ingredientResult.allergens, ingredientResult.overrideMarkers || [])
      );
    }

    return this.analysisEntryHasDetectedAllergen(result.allergens, []);
  }

  analysisEntryHasDetectedAllergen(allergens: Array<{ id?: string | null }>, overrideMarkers: Array<{ isAllergen: boolean }>) {
    return (allergens || []).length > 0 || (overrideMarkers || []).some((marker) => marker.isAllergen);
  }
}
